import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  Button,
  gridClasses,
  Modal,
  Box,
  Typography,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import PhoneIcon from "@mui/icons-material/Phone";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";
const outlinedBtn = {
  height: 30,
  px: 1.5,
  borderRadius: "8px",
  border: "1px solid #D5DEE7",
  background: "#FFFFFF",
  color: INK,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#EAF2FB", borderColor: "#C9DEF3", color: "#1B4F82", boxShadow: "none" },
};

const COLUMN_LABELS = {
  leadId: "Lead ID",
  name: "Lead Name",
  phone: "Phone Number",
  email: "Email",
  leadSource: "Lead Source",
  location: "Location",
  preferredPropertyType: "Property Type",
  minBudget: "Min. Budget",
  maxBudget: "Max. Budget",
  timelineToBuy: "Timeline to Buy",
  purpose: "Purpose",
  leadPriority: "Priority",
  bookingAmount: "Booking Amount",
  finalPrice: "Final Price",
  campaignName: "Campaign Name",
  channel: "Channel",
  campaignBudget: "Campaign Budget",
  campaignDescription: "Campaign Description",
  utmSource: "UTM",
  lastFollowUp: "Last Follow Up",
  nextFollowUp: "Next Follow Up",
  campaignStartDate: "Campaign Start Date",
  campaignEndDate: "Campaign End Date",
  status: "Status",
};

const PartnerLeadTable = ({ projectId }) => {
  const [leads, setLeads] = useState([]);
  const [columns, setColumns] = useState([]);
  const [role, setRole] = useState();
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const isMobile = useMediaQuery("(max-width: 750px)");
  const decoded = jwtDecode(storedUser.accessToken);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/partner/leads`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      const data = res.data;
      setLeads(data);

      // Extract dynamic columns (non-null keys)
      if (data.length > 0) {
        const nonNullColumns = Object.keys(data[0]).filter((key) =>
          data.some(
            (row) =>
              row[key] !== null &&
              row[key] !== "" &&
              key !== "assignedBy" &&
              key !== "assignedTo" &&
              key !== "reminder" &&
              key !== "utmSource" &&
              key !== "utmMedium" &&
              key !== "utmCampaign" &&
              key !== "utmContent" &&
              key !== "utmTerm",
          ),
        );

        setColumns(nonNullColumns);
      }
    } catch (err) {
      setLeads([]);
      console.log(err);
    }
  };

  useEffect(() => {
    if (!storedUser?.accessToken) return;

    const role = decoded.userRole[0];
    setRole(role);

    fetchLeads();
  }, []);

  const gridColumns = columns
    .filter((key) => key !== "isLead" && key !== "countryCode")
    .map((key) => {
      if (key === "leadId") {
        return {
          field: key,
          headerName: "Lead ID",
          minWidth: 180,
          renderCell: (params) => (
            <span style={{ fontFamily: MONO, fontSize: 12.5, color: "#3D4C60" }}>
              {params.row.leadId} {params.row.isLead ? "⭐" : null}
            </span>
          ),
        };
      }
      if (key === "phone" || key === "phoneNumber") {
        return {
          field: key,
          headerName: COLUMN_LABELS[key] || key.replace(/([A-Z])/g, " $1"),
          minWidth: 180,
          renderCell: (params) => {
            let raw = params.value || "";
            let cleaned = raw.replace(/\D/g, "");
            let formatted =
              params.row.countryCode != null &&
              params.row.countryCode.includes("+")
                ? `${params.row.countryCode}${cleaned}`
                : `+${cleaned}`;
            const whatsappUrl = `https://api.whatsapp.com/send?phone=${cleaned}&text=${encodeURIComponent(
              "Hi",
            )}`;
            const callUrl = `tel:${formatted}`;

            return (
              <div style={{ display: "flex", fontFamily: MONO, fontSize: 12.5 }}>
                {formatted}

                {/* WhatsApp Button */}
                {/* <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: "#25D366",
                    color: "#fff",
                    borderRadius: "6px",
                    textDecoration: "none",
                    fontSize: "12px",
                    height: "25px",
                    display: "flex",
                    alignItems: "center",
                    margin: "auto",
                  }}
                >
                  WhatsApp
                </a> */}
              </div>
            );
          },
        };
      }

      // Normal columns
      return {
        field: key,
        headerName: COLUMN_LABELS[key] || key.replace(/([A-Z])/g, " $1"),
        minWidth: 180,
        ...(key === "name"
          ? { renderCell: (params) => <span style={{ fontWeight: 700, color: INK }}>{params.value}</span> }
          : {}),
      };
    });

  gridColumns.push({
    field: "actions",
    headerName: "Actions",
    width: 400,
    sortable: false,
    renderCell: (params) => (
      <>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/partner/${params.row.leadId}`)}
          sx={outlinedBtn}
        >
          View
        </Button>
      </>
    ),
  });

  return (
    <div style={{ fontFamily: FONT, color: INK }}>
      <h2
        style={{
          margin: "0 0 16px",
          fontFamily: FONT,
          fontSize: 27,
          fontWeight: 800,
          letterSpacing: "-0.028em",
          color: INK,
        }}
      >
        My Leads
      </h2>
      {isMobile && (
        <div className="App">
          {leads.map((lead, idx) => (
            <Paper
              key={idx}
              elevation={0}
              style={{
                padding: "4px 14px",
                marginBottom: "10px",
                borderRadius: "14px",
                backgroundColor: "#FFFFFF",
                border: `1px solid ${LINE}`,
              }}
            >
              {columns
                .filter((key) => key !== "isLead" && key !== "reminder" && key !== "countryCode")
                .map((col) => {
                  let cleaned = (lead.phone || "").replace(/\D/g, "");
                  let formatted =
                    lead.countryCode !== null && lead.countryCode.includes("+")
                      ? lead.countryCode + cleaned
                      : `+${cleaned}`;
                  return (
                    <div key={idx} className="card" style={{ padding: "10px 0", boxShadow: "none", border: 0, background: "transparent" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <Typography
                            fontSize={"17px"}
                            fontWeight={"800"}
                            onClick={() => {
                              const url = `/partner/${lead.leadId}`;
                              window.open(url, "_blank", "noopener,noreferrer");
                            }}
                            sx={{
                              cursor: "pointer",
                              fontFamily: FONT,
                              letterSpacing: "-0.02em",
                              color: INK,
                              "&:active": {
                                color: PRIMARY,
                                borderRadius: "6px",
                                padding: "2px 6px",
                              },
                            }}
                          >
                            {lead.name}
                          </Typography>
                        </div>
                      </div>
                      <Typography
                        color={MUTED}
                        fontSize={"13.5px"}
                        fontWeight={500}
                        mt={1.25}
                        sx={{ display: "flex", alignItems: "center", gap: 1.25, fontFamily: FONT }}
                      >
                        <CallIcon sx={{ fontSize: 16, color: "#8A97A8" }} />{" "}
                        <a
                          href={`tel:${formatted}`}
                          style={{
                            color: "#1B4F82",
                            textDecoration: "none",
                            fontFamily: MONO,
                            fontSize: 13,
                          }}
                        >
                          {formatted}
                        </a>
                        {/* <IconButton
                          size="small"
                          onClick={(e) =>
                            handleOpen(
                              e,
                              (lead.countryCode !== null &&
                              lead.countryCode.includes("+")
                                ? lead.countryCode.substring(1)
                                : lead.countryCode) +
                                lead.phone.replace(/\D/g, ""),
                            )
                          }
                          sx={{
                            background: "#059669",
                            color: "#fff",
                            height: "fit-content",
                            borderRadius: "15px",
                            "&:hover": { backgroundColor: "#059669" },
                            "&:active": { backgroundColor: "#059669" },
                            "&:focus": { backgroundColor: "#059669" },
                          }}
                        >
                          <WhatsAppIcon sx={{ width: 20, height: 20 }} />
                          <ArrowDropDownIcon />
                        </IconButton> */}
                        {/* <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleClose}
                          slotProps={{
                            paper: {
                              sx: {
                                boxShadow: "none",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                mt: 1,
                                minWidth: 180,
                              },
                            },
                          }}
                        >
                          {template?.length === 0 ? (
                            <MenuItem sx={{ fontSize: "12px" }}>
                              No templates added
                            </MenuItem>
                          ) : (
                            template?.map((template) => (
                              <MenuItem
                                key={template.templateId}
                                onClick={() => {
                                  const whatsappUrl = `https://api.whatsapp.com/send?phone=${selectedNumber}&text=${encodeURIComponent(
                                    template.template,
                                  )}`;
                                  window.open(whatsappUrl, "_blank");
                                  handleClose();
                                }}
                                sx={{
                                  fontSize: "12px",
                                  padding: "1px 10px",
                                  borderRadius: "6px",
                                  mx: 0.5,
                                  my: 0.3,
                                }}
                              >
                                {template.title}
                              </MenuItem>
                            ))
                          )}
                          <MenuItem
                            onClick={() => {
                              const whatsappUrl = `https://api.whatsapp.com/send?phone=${selectedNumber}`;
                              window.open(whatsappUrl, "_blank");
                              handleClose();
                            }}
                            sx={{
                              fontSize: "12px",
                              padding: "6px 10px",
                              borderRadius: "6px",
                              mx: 0.5,
                              my: 0.3,
                              borderTop:
                                template?.length > 0
                                  ? "1px solid #e0e0e0"
                                  : "none",
                              "&:hover": { backgroundColor: "#f5f5f5" },
                            }}
                          >
                            Custom Message
                          </MenuItem>
                        </Menu> */}
                      </Typography>
                      <Typography
                        color={MUTED}
                        fontSize={"13.5px"}
                        fontWeight={500}
                        mt={0.75}
                        mb={0.75}
                        sx={{ display: "flex", alignItems: "center", gap: 1.25, fontFamily: FONT, wordBreak: "break-all" }}
                      >
                        <EmailIcon sx={{ fontSize: 16, color: "#8A97A8" }} /> {lead.email}
                      </Typography>
                      <Typography
                        color={MUTED}
                        fontSize={"12px"}
                        fontWeight={500}
                        mb={1.25}
                        sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: MONO }}
                      >
                        Organization ID: {lead.orgId}
                      </Typography>
                      <hr style={{ border: 0, borderTop: "1px solid #EDF1F5", margin: "0 0 10px" }} />
                      <div style={{ display: "flex", justifyContent: "end" }}>
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          onClick={() => {
                            const url = `/partner/${lead.leadId}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          sx={{ ...outlinedBtn, height: 40, px: 2.5 }}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              {/* {role !== "VT1" && role !== "VT2" && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/partner/${lead.leadId}`)}
                  >
                    View
                  </Button>
                </>
              )} */}
            </Paper>
          ))}
        </div>
      )}
      {!isMobile && (
        <Box sx={{ background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden" }}>
        <DataGrid
          rows={leads}
          columns={gridColumns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10]}
          disableRowSelectionOnClick
          disableColumnSelector
          disableDensitySelector
          slots={{ toolbar: CustomToolbar }}
          getRowId={(row) => row.leadId}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          rowHeight={50}
          sx={{
            position: "relative",
            border: "none",
            fontFamily: `${FONT} !important`,
            color: INK,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "700 !important",
              fontSize: 10.5,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              color: MUTED,
            },
            "& .MuiDataGrid-columnHeaders": {
              background: "#F7F9FC !important",
              color: MUTED,
              fontSize: "14px",
              borderRadius: 0,
            },
            "& .MuiDataGrid-columnHeader": { background: "#F7F9FC" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#F7F9FC" },
            "& .MuiDataGrid-cell": { fontSize: "13.5px", border: "none", borderBottom: "1px solid #F1F4F8", display: "flex", alignItems: "center" },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            [`& .${gridClasses.row}.even`]: { backgroundColor: "#FFFFFF" },
            "& .MuiInput-underline:before": { borderBottom: "none !important" },
            "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${LINE}`, fontFamily: MONO },
            "& .MuiTablePagination-displayedRows": { fontFamily: MONO, fontSize: 12, color: MUTED },
          }}
        />
        </Box>
      )}
    </div>
  );
};

export default PartnerLeadTable;

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end", p: "10px 12px", borderBottom: "1px solid #EDF1F5" }}>
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #D5DEE7",
          borderRadius: "10px",
          padding: "2px 10px",
          background: "#FFFFFF",
          "&:hover": {
            borderColor: "#B4C6DA",
          },
          "& .MuiInputBase-root": {
            borderRadius: "10px",
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: 13.5,
          },
          "& .MuiSvgIcon-root": { color: "#8A97A8", fontSize: 18 },
          width: 300,
        }}
      />
      {/* <GridToolbarFilterButton /> */}
    </GridToolbarContainer>
  );
}
